/**
 * Общие названия, форматтеры и бейджи раздела «Банковские выписки»
 * (карточка BL-2026-08-15-2024, техспека §7).
 *
 * Flow: компоненты раздела берут отсюда слова для кодов статусов, видов и
 *       принадлежности, а также форматирование дат, срезов и сумм.
 *
 * Зависимости:
 *   - react: useLatestRequest держит номер последнего запроса блока;
 *   - ./DashboardPanel: money и formatMoment — ЕДИНЫЕ форматтеры денег и
 *     моментов UTC во всём интерфейсе; своя копия разошлась бы молча.
 *
 * Связанные файлы:
 *   - src/BankStatementsSection.jsx и блоки раздела — потребители;
 *   - src/BankTotalsPanel.jsx, src/DashboardPanel.jsx — блоки итогов;
 *   - docs/specs/BL-2026-08-15-2024-bank-statements.md — §6 контракт, §7.3 цвета.
 *
 * # ПОЧЕМУ НАЗВАНИЯ ЗДЕСЬ, ЕСЛИ СЕРВЕР ИХ ОТДАЁТ
 *
 * Сервер отдаёт `status_title`, `kind_title`, `funds_owner_title` рядом с
 * кодом, и показывается ИМЕННО серверное название, когда оно есть. Таблицы
 * ниже — запасной текст на случай, когда названия в ответе нет (форма
 * `duplicate_of` в §6.2 короче полной). Код без названия показывается сам
 * собой — так на экране видно, что сервер знает значение, которого нет
 * здесь, а не пусто.
 */

import { useCallback, useEffect, useRef } from "react";

import { formatMoment, money } from "./DashboardPanel";

// Переэкспорт: блоки раздела берут форматтеры из одного места.
export { formatMoment, money };

/** Потолок размера выписки — тот же, что проверяет сервер (§6.2). */
export const MAX_STATEMENT_BYTES = 5 * 1024 * 1024;

/** Статусы загрузки: запасное название и класс бейджа (§3.10, §7.3). */
export const STATUS_META = {
  accepted: { title: "Принята", cls: "bs-status-accepted" },
  accepted_with_warnings: {
    title: "Принята с предупреждениями",
    cls: "bs-status-warnings",
  },
  invariant_failed: { title: "Инвариант не сошёлся", cls: "bs-status-invariant" },
  failed: { title: "Не разобрана", cls: "bs-status-failed" },
  duplicate: { title: "Дубликат", cls: "bs-status-duplicate" },
};

/** Коды статусов в порядке фильтра списка загрузок. */
export const STATUS_CODES = Object.keys(STATUS_META);

/** Принадлежность денег на счёте (§3.2): короткое название для бейджа. */
export const OWNER_META = {
  company: { title: "Собственный", cls: "bs-owner-company" },
  client: {
    title: "Клиентский — не ликвидность компании",
    cls: "bs-owner-client",
  },
  unknown: { title: "Не размечен", cls: "bs-owner-unknown" },
};

/** Вид операции «нашим взглядом» (§3.8). */
export const KIND_META = {
  incoming: { title: "Поступление", cls: "bs-kind-in" },
  outgoing: { title: "Списание", cls: "bs-kind-out" },
  fee: { title: "Комиссия", cls: "bs-kind-fee" },
  internal_transfer: { title: "Внутренний перевод", cls: "bs-kind-internal" },
  fx_exchange: { title: "Обмен валюты", cls: "bs-kind-fx" },
};

/** Исход строки файла (§6.5). */
export const OUTCOME_TITLES = {
  inserted: "Новая",
  known: "Уже была",
  conflict: "Конфликт",
  not_promoted: "Не принята",
};

/** Инвариант счёта (§3.10): «Сошёлся» / «Не сошёлся» / «Неприменим». */
export const INVARIANT_META = {
  passed: { title: "Сошёлся", cls: "bs-status-accepted" },
  failed: { title: "Не сошёлся", cls: "bs-status-invariant" },
  not_applicable: { title: "Неприменим", cls: "bs-status-duplicate" },
};

/**
 * Дата YYYY-MM-DD русской раскладкой.
 *
 * @param {string|null} date — дата из ответа
 * @returns {string} «30.09.2026» либо прочерк
 *
 * Разбор строки, а не new Date(): дата без момента, отданная в Date,
 * становится полночью UTC и в зоне браузера западнее UTC показала бы
 * предыдущий день.
 */
export function formatDate(date) {
  if (!date) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(date);
  if (!m) return date;
  return `${m[3]}.${m[2]}.${m[1]}`;
}

/** Период выписки «с – по». */
export function formatPeriod(from, to) {
  if (!from && !to) return "—";
  return `${formatDate(from)} – ${formatDate(to)}`;
}

/**
 * Срез выписки с учётом точности.
 *
 * @param {string|null} cutAt — момент среза RFC 3339 UTC
 * @param {string} precision — 'moment' (банк дал время) | 'day' (только дата)
 * @returns {string} момент UTC либо дата
 *
 * При точности `day` время НЕ показывается: банк его не дал, и «20:00 UTC»
 * на экране выдумало бы момент, которого в выписке нет (BR-011 в применении
 * ко времени, техспека §3.7).
 */
export function formatCut(cutAt, precision) {
  if (!cutAt) return "—";
  if (precision === "day") return formatDate(cutAt.slice(0, 10));
  return formatMoment(cutAt);
}

/** Размер файла: «101,2 КБ». */
export function formatBytes(n) {
  if (n === null || n === undefined) return "—";
  if (n < 1024) return `${n} Б`;
  const kb = n / 1024;
  if (kb < 1024) return `${kb.toLocaleString("ru-RU", { maximumFractionDigits: 1 })} КБ`;
  return `${(kb / 1024).toLocaleString("ru-RU", { maximumFractionDigits: 1 })} МБ`;
}

/**
 * Сумма с валютой либо прочерк.
 *
 * @param {string|null} amount — сумма строкой
 * @param {string} currency — код валюты
 * @returns {string} «7 004 940,00 RUB» либо «—»
 *
 * Прочерк, а не «неизвестно»: в карточке выписки пустое значение означает
 * «банк эту величину не печатает», и об этом говорит подсказка рядом.
 */
export function formatAmount(amount, currency = "") {
  if (amount === null || amount === undefined || amount === "") return "—";
  const text = money(amount);
  return currency ? `${text} ${currency}` : text;
}

/**
 * Совпадают ли две десятичные строки как числа.
 *
 * @param {string} a
 * @param {string} b
 * @returns {boolean}
 *
 * Сравнение СТРОК после нормализации, без перевода в число: «банк vs разбор»
 * — проверка точная (техспека §3.10, big.Rat без допуска), и подсветка
 * расхождения не должна гасить разницу в копейку округлением double.
 */
export function sameDecimal(a, b) {
  return normalizeDecimal(a) === normalizeDecimal(b);
}

function normalizeDecimal(v) {
  let s = String(v).trim();
  let sign = "";
  if (s.startsWith("-")) {
    sign = "-";
    s = s.slice(1);
  } else if (s.startsWith("+")) {
    s = s.slice(1);
  }
  let [int, frac = ""] = s.split(".");
  int = int.replace(/^0+(?=\d)/, "");
  frac = frac.replace(/0+$/, "");
  if (int === "0" && frac === "") sign = "";
  return `${sign}${int}${frac ? `.${frac}` : ""}`;
}

/**
 * Бейдж статуса загрузки.
 *
 * @param {string} status — код статуса
 * @param {string} title — название с сервера (необязательно)
 *
 * Рядом с цветом всегда слово (§7.3): статус не передаётся только цветом.
 */
export function StatusBadge({ status, title }) {
  const meta = STATUS_META[status];
  return (
    <span className={`badge bs-badge ${meta ? meta.cls : ""}`}>
      {title || (meta ? meta.title : status)}
    </span>
  );
}

/** Бейдж принадлежности денег на счёте. */
export function OwnerBadge({ owner }) {
  const meta = OWNER_META[owner] || OWNER_META.unknown;
  return <span className={`badge bs-badge ${meta.cls}`}>{meta.title}</span>;
}

/** Бейдж инварианта счёта. */
export function InvariantBadge({ invariant }) {
  const meta = INVARIANT_META[invariant];
  return (
    <span className={`badge bs-badge ${meta ? meta.cls : ""}`}>
      Инвариант: {meta ? meta.title : invariant}
    </span>
  );
}

/** Бейдж вида операции. */
export function KindBadge({ kind, title, feeKindTitle }) {
  const meta = KIND_META[kind];
  return (
    <span className={`badge bs-badge ${meta ? meta.cls : ""}`}>
      {title || (meta ? meta.title : kind)}
      {feeKindTitle ? ` · ${feeKindTitle.toLowerCase()}` : ""}
    </span>
  );
}

/**
 * Формы слова для числительного: «1 счёт», «2 счёта», «5 счетов».
 *
 * Порядок форм — [одна, две-четыре, пять и больше]. Наборы ниже — слова
 * раздела; другие блоки передают свои.
 */
export const WORD_FORMS = {
  account: ["счёт", "счёта", "счетов"],
  operation: ["операция", "операции", "операций"],
  line: ["строка", "строки", "строк"],
  file: ["файл", "файла", "файлов"],
  unrecognizedLine: [
    "нераспознанная строка",
    "нераспознанные строки",
    "нераспознанных строк",
  ],
};

/**
 * Форма слова, согласованная с числом, по правилам русского языка.
 *
 * @param {number|string} n — количество (строка с сервера допускается)
 * @param {string[]} forms — [одна, две-четыре, пять и больше]
 * @returns {string} форма слова без числа
 *
 * 11–14 — всегда «пять и больше» («11 счетов», не «11 счёт»). Дробное или
 * нечисловое значение получает вторую форму («1,5 счёта»): у дробей
 * управляет слово «часть», а не последняя цифра.
 */
export function pluralForm(n, forms) {
  const num = Math.abs(Number(n));
  if (!Number.isFinite(num) || !Number.isInteger(num)) return forms[1];
  const mod100 = num % 100;
  const mod10 = num % 10;
  if (mod100 >= 11 && mod100 <= 14) return forms[2];
  if (mod10 === 1) return forms[0];
  if (mod10 >= 2 && mod10 <= 4) return forms[1];
  return forms[2];
}

/**
 * Число со словом: «1 счёт», «22 операции», «0 нераспознанных строк».
 *
 * @param {number|string|null} n — количество; null/undefined читается как 0
 * @param {string[]} forms — формы слова (см. WORD_FORMS)
 * @returns {string}
 */
export function countWord(n, forms) {
  const value = n === null || n === undefined ? 0 : n;
  return `${value} ${pluralForm(value, forms)}`;
}

/**
 * Пометка «скрыто» на месте поля, которое сервер не отдал роли.
 *
 * Сервер для роли viewer очищает `raw_text`, текст нераспознанной строки и
 * реквизиты контрагента (в выписке там ФИО физических лиц) и ставит в ответ
 * признак `personal_data_hidden = true`. Пустое место читалось бы как «банк
 * ничего не напечатал» — поэтому словами.
 */
export function HiddenValue() {
  return (
    <span
      className="state-unknown"
      title="Скрыто для вашей роли: строка выписки может содержать ФИО физического лица"
    >
      скрыто
    </span>
  );
}

/**
 * Пусто ли поле потому, что сервер его СКРЫЛ, а не потому, что значения нет.
 *
 * @param {*} value — поле операции или строки выписки
 * @param {boolean} [personalDataHidden] — признак `personal_data_hidden`
 *        ответа (карточки §6.4, страницы операций §6.5, выборки §6.6)
 * @returns {boolean} true — показывать «скрыто»
 *
 * Контракт (после правки backend, находка 3 ревью): скрытое поле приходит
 * ПУСТОЙ СТРОКОЙ, а о скрытии говорит признак `personal_data_hidden` на
 * уровне ОТВЕТА — не операции и не строки. Поэтому:
 *   * признак true и поле пусто ("" / null / undefined) → «скрыто»;
 *   * признак true, но поле непусто → значение (сервер его не скрывал —
 *     показывать то, что пришло, честнее, чем прятать);
 *   * признак false/нет, поле "" → прочерк: в выписке значения нет;
 *   * null без признака → тоже «скрыто»: так скрытие выглядело в прежней
 *     редакции контракта, и пустота на месте null читалась бы как «в
 *     выписке ничего не было».
 */
export function isHidden(value, personalDataHidden = false) {
  if (value === null) return true;
  if (!personalDataHidden) return false;
  return value === "" || value === undefined;
}

/**
 * Имя контрагента операции: значение, «скрыто» либо прочерк.
 *
 * @param {object} tx — операция (§6.5 transaction либо §6.6)
 * @param {boolean} [hidden] — признак `personal_data_hidden` ответа
 */
export function CounterpartyName({ tx, hidden = false }) {
  if (tx.counterparty_name) return tx.counterparty_name;
  if (isHidden(tx.counterparty_name, hidden)) return <HiddenValue />;
  return "—";
}

/**
 * Ключ строки списка React с запасным значением.
 *
 * @param {*} id — идентификатор с сервера (может быть null: например,
 *        account_id счёта несошедшегося файла, ещё не заведённого в
 *        справочник)
 * @param {string} prefix — пространство ключа, чтобы запасные ключи разных
 *        видов не совпали с настоящими id
 * @param {...*} parts — поля, однозначно задающие строку без id
 * @returns {string|number} id, если он есть, иначе составной ключ
 *
 * Зачем: `key={null}` у нескольких строк React склеивает их в одну и
 * предупреждает в консоли; составной ключ из реквизитов строки устойчив
 * между перерисовками, в отличие от индекса массива.
 */
export function rowKey(id, prefix, ...parts) {
  if (id !== null && id !== undefined) return id;
  return `${prefix}:${parts.map((p) => (p === null || p === undefined ? "" : String(p))).join("|")}`;
}

/**
 * Защита от позднего ответа: применять результат только ПОСЛЕДНЕГО запроса.
 *
 * @returns {function(): function(): boolean} begin — вызывается перед
 *          запросом и возвращает проверку «этот запрос всё ещё последний и
 *          блок на экране»
 *
 * Зачем: при быстрой смене фильтров (или опросе по таймеру рядом со сменой
 * отбора) ответы приходят не в порядке отправки, и поздний ответ по
 * СТАРОМУ отбору перетёр бы свежий — на экране оказались бы операции не
 * того периода под фильтром нужного. Размонтирование тоже делает все
 * запросы устаревшими: ответ закрытой панели некуда применять.
 *
 * Сам запрос не обрывается (api.js сигнала отмены не принимает) — он лишь
 * игнорируется; для чтений GET этого достаточно.
 */
export function useLatestRequest() {
  const seq = useRef(0);
  useEffect(
    () => () => {
      seq.current += 1;
    },
    [],
  );
  return useCallback(() => {
    seq.current += 1;
    const mine = seq.current;
    return () => seq.current === mine;
  }, []);
}
